import styled from 'styled-components'
import { Input as AntInput } from 'antd'
import { Pencil } from 'lucide-react'
import { SPACING, BORDER_RADIUS, TYPOGRAPHY, TRANSITIONS } from '../../../../styles/theme'
import { TEXT_STYLES } from '../../../../constants/mindmapConfig'

// Re-export Button from antd as AntButton
export { Button as AntButton } from 'antd'

export const DropdownContent = styled.div`
  background: var(--color-bg-elevated);
  border-radius: ${BORDER_RADIUS.md};
  box-shadow: var(--shadow-dropdown);
  min-width: 240px;
  max-width: 280px;
  overflow: hidden;
  position: relative;
  /* z-index is handled by the parent Dropdown component via Z_INDEX.dropdown */
`

export const LabelList = styled.div`
  max-height: 300px;
  overflow-y: auto;
`

export const LabelItem = styled.div<{ $isSelected: boolean }>`
  display: flex;
  align-items: center;
  gap: ${SPACING.sm};
  padding: ${SPACING.sm} ${SPACING.md};
  cursor: pointer;
  transition: ${TRANSITIONS.fast};

  &:hover {
    background-color: var(--color-bg-hover);
  }

  ${(props) =>
    props.$isSelected &&
    `
    background-color: var(--color-bg-hover);
  `}
`

export const DeleteButtonWrapper = styled.div`
  margin-left: auto;
  flex-shrink: 0;
`

export const Checkbox = styled.div<{ $isChecked: boolean; $color: string }>`
  width: 16px;
  height: 16px;
  border-radius: ${BORDER_RADIUS.sm};
  border: 2px solid var(--color-border-dark);
  background-color: ${(props) =>
    props.$isChecked ? 'var(--color-text-secondary)' : 'var(--color-bg-elevated)'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

export const LabelColorDot = styled.div<{ $color: string }>`
  width: 12px;
  height: 12px;
  border-radius: ${BORDER_RADIUS.sm};
  background-color: ${(props) => props.$color};
  flex-shrink: 0;
`

export const LabelText = styled.span`
  font-size: ${TYPOGRAPHY.body.fontSize};
  color: var(--color-text-primary);
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const Divider = styled.div`
  height: 1px;
  background-color: var(--color-border);
`

export const CreateButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${SPACING.sm};
  padding: ${SPACING.sm} ${SPACING.md};
  width: 100%;
  border: none;
  background: var(--color-bg-elevated);
  color: var(--color-text-secondary);
  font-size: ${TYPOGRAPHY.body.fontSize};
  cursor: pointer;
  transition: ${TRANSITIONS.fast};

  &:hover {
    background-color: var(--color-bg-hover);
  }
`

export const CreateForm = styled.div`
  padding: ${SPACING.lg};
  background-color: var(--color-bg-elevated);
`

export const FormSection = styled.div`
  margin-bottom: ${SPACING.lg};

  &:last-child {
    margin-bottom: 0;
  }
`

export const FormLabel = styled.div`
  font-size: ${TEXT_STYLES.label.fontSize};
  font-weight: ${TEXT_STYLES.label.fontWeight};
  color: var(--color-text-secondary);
  text-transform: ${TEXT_STYLES.label.textTransform};
  letter-spacing: ${TEXT_STYLES.label.letterSpacing};
  margin-bottom: ${SPACING.sm};
`

export const StyledInput = styled(AntInput)`
  width: 100%;
  border-radius: ${BORDER_RADIUS.sm};

  &:focus,
  &:hover {
    border-color: #3b82f6;
  }
`

export const ColorPalette = styled.div`
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 6px;
  padding: 0 2px;
`

export const PencilIcon = styled(Pencil)`
  z-index: 0;
  position: relative;
  pointer-events: none;
  color: var(--color-text-secondary);
`

export const DropdownWrapper = styled.div`
  /* z-index is handled by the parent Dropdown component via Z_INDEX.dropdown */
`

export const ColorOption = styled.button<{
  $color: string
  $isSelected: boolean
  $isCustom?: boolean
  $customColor?: string
}>`
  width: 22px;
  height: 22px;
  border-radius: ${BORDER_RADIUS.sm};
  background: ${(props) => {
    if (props.$isCustom) {
      // If a custom color is selected, show that color, otherwise use elevated bg
      if (
        props.$customColor &&
        !['#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6', '#a855f7', '#ec4899'].includes(
          props.$customColor
        )
      ) {
        return props.$customColor
      }
      return 'var(--color-bg-elevated)'
    }
    return props.$color
  }};
  border: ${(props) => {
    if (props.$isCustom) {
      return '2px solid var(--color-border-dark)'
    }
    return '2px solid var(--color-border)'
  }};
  cursor: pointer;
  transition: ${TRANSITIONS.all};
  position: relative;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${(props) => {
    if (!props.$isSelected) return 'none'
    // Get the color to use for the shadow
    const shadowColor =
      props.$isCustom &&
      props.$customColor &&
      !['#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6', '#a855f7', '#ec4899'].includes(
        props.$customColor
      )
        ? props.$customColor
        : props.$color
    // Convert hex to rgba with low opacity
    const hex = shadowColor.replace('#', '')
    const r = parseInt(hex.slice(0, 2), 16)
    const g = parseInt(hex.slice(2, 4), 16)
    const b = parseInt(hex.slice(4, 6), 16)
    return `0 0 0 4px rgba(${r}, ${g}, ${b}, 0.25)`
  }};

  &:hover {
    transform: scale(1.1);
    border-color: var(--color-border-dark);
  }
`

export const CustomColorInput = styled.input`
  position: absolute;
  opacity: 0;
  width: 100%;
  height: 100%;
  cursor: pointer;
  z-index: 1;
`

export const ButtonGroupContainer = styled.div`
  display: flex;
  gap: ${SPACING.sm};
  justify-content: flex-end;
  margin-top: ${SPACING.md};
`
